import React, { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router";
import Header from "../../components/Header";
import Loading from "../../components/Loading";
import BackButton from "../../components/BackButton";
import { getUserById, UserDetail } from "../../services/userService";
import { getSubscriptionStatusLabel } from "../../utils/subscriptionStatusLabel";

const InfoRow: React.FC<{ label: string; value: React.ReactNode }> = ({
  label,
  value,
}) => (
  <div className="border-b border-gray-300 pb-2">
    <p className="text-xs text-gray-500">{label}</p>
    <p className="text-sm font-medium text-gray-800">{value ?? "N/A"}</p>
  </div>
);

const Section: React.FC<{ title: string; children: React.ReactNode }> = ({
  title,
  children,
}) => (
  <div className="bg-white rounded-lg shadow p-4 mb-4">
    <h2 className="text-sm font-semibold text-gray-800 mb-3">{title}</h2>
    <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">{children}</div>
  </div>
);

const UserProfile: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [user, setUser] = useState<UserDetail | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!id) return;
    (async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await getUserById(id);
        setUser(result);
      } catch {
        setError("Could not load this user's profile.");
      } finally {
        setLoading(false);
      }
    })();
  }, [id]);

  const statusBadgeClass = (u: UserDetail) =>
    u.isBlocked
      ? "bg-red-100 text-red-600"
      : u.isVerified
        ? "bg-green-100 text-green-600"
        : "bg-gray-100 text-gray-600";

  const statusBadgeLabel = (u: UserDetail) =>
    u.isBlocked ? "Blocked" : u.isVerified ? "Active" : "Pending";

  return (
    <div className="p-4 md:p-6 bg-gray-100 min-h-screen">
      <Header title="Member Profile" />
      <div className="mb-4">
        <BackButton onClick={() => navigate(-1)} />
      </div>

      {loading ? (
        <Loading message="Loading profile..." />
      ) : error || !user ? (
        <div className="bg-white rounded-lg shadow p-6 text-center">
          <p className="text-sm text-red-600 mb-2">
            {error || "User not found."}
          </p>
          <button
            onClick={() => navigate("/users")}
            className="text-red-600 hover:underline text-xs"
          >
            Back to Users
          </button>
        </div>
      ) : (
        <>
          <div className="bg-white rounded-lg shadow p-4 mb-4 flex items-center justify-between flex-wrap gap-2">
            <div>
              <h2 className="text-lg font-semibold text-gray-800">
                {[user.name, user.surname].filter(Boolean).join(" ") ||
                  "N/A"}
              </h2>
              <p className="text-sm text-gray-500">{user.email}</p>
              <p className="text-xs text-gray-400">ID: {user.id}</p>
            </div>
            <div className="flex items-center gap-2">
              <span
                className={`px-2 py-1 text-xs rounded ${statusBadgeClass(user)}`}
              >
                {statusBadgeLabel(user)}
              </span>
              <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-600">
                {getSubscriptionStatusLabel(user.subscriptionStatus)}
              </span>
            </div>
          </div>

          <Section title="Account Info">
            <InfoRow label="Role" value={user.role} />
            <InfoRow label="Phone" value={user.phoneNumber} />
            <InfoRow label="Gender" value={user.gender} />
            <InfoRow label="Age" value={user.age} />
            <InfoRow
              label="Member Since"
              value={
                user.createdAt
                  ? new Date(user.createdAt).toISOString().split("T")[0]
                  : null
              }
            />
            <InfoRow
              label="Last Login"
              value={
                user.lastLoginAt
                  ? new Date(user.lastLoginAt).toISOString().split("T")[0]
                  : "-"
              }
            />
          </Section>

          <Section title="Subscription">
            <InfoRow
              label="Subscription Type"
              value={user.typeOfSubscription}
            />
            <InfoRow
              label="Current Plan"
              value={user.currentSubscription?.type}
            />
            <InfoRow
              label="Purchase Date"
              value={
                user.currentSubscription?.purchaseDate
                  ? new Date(user.currentSubscription.purchaseDate)
                      .toISOString()
                      .split("T")[0]
                  : null
              }
            />
            <InfoRow
              label="Is Premium"
              value={user.isPremium ? "Yes" : "No"}
            />
            <InfoRow label="Premium By" value={user.premiumBy} />
            <InfoRow
              label="Subscription Expires"
              value={
                user.subscriptionExpiresAt
                  ? new Date(user.subscriptionExpiresAt)
                      .toISOString()
                      .split("T")[0]
                  : null
              }
            />
          </Section>

          <Section title="Fitness Basics">
            <InfoRow label="Height" value={user.height} />
            <InfoRow label="Weight" value={user.weight} />
            <InfoRow
              label="Minutes for Train"
              value={user.minutesForTrain}
            />
            <InfoRow label="Body Type" value={user.bodyType} />
          </Section>
        </>
      )}
    </div>
  );
};

export default UserProfile;
